'use client'

import { ReactNode, useState, useTransition } from 'react'
import { useTranslations } from 'next-intl'
import { Box, Button, Flex, Stack, Text, Title } from '@mantine/core'
import { DefaultValues, FieldValues, FormProvider, Path, Resolver, useForm, UseFormReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ZodType } from 'zod'

import classes from './multistep.module.css'

export type MultistepForm<T extends FieldValues> = UseFormReturn<T, unknown, T>

export interface MultistepStep<T extends FieldValues> {
    key: string
    label: string
    description?: string
    fields?: Path<T>[]
    Component: (props: { form: MultistepForm<T> }) => ReactNode
}

interface Props<T extends FieldValues> {
    steps: MultistepStep<T>[]
    defaultValues: DefaultValues<T>
    onSubmit: (values: T) => Promise<void> | void
    schema?: ZodType<T>
    submitLabel?: string
    onCancel?: () => void
}

export default function Multistep<T extends FieldValues>({
    steps,
    defaultValues,
    onSubmit,
    schema,
    submitLabel,
    onCancel
}: Props<T>) {
    const t = useTranslations('common.actions')
    const tStates = useTranslations('common.states')
    const tStep = useTranslations('common.multistep')
    const resolver = schema ? (zodResolver(schema as any) as Resolver<T>) : undefined
    const form = useForm<T>({ defaultValues, mode: 'onTouched', resolver }) as MultistepForm<T>
    const [active, setActive] = useState(0)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [submitting, startTransition] = useTransition()

    const current = steps[active]
    const isLast = active === steps.length - 1
    const StepComponent = current.Component

    const goBack = () => {
        setActive((a) => Math.max(0, a - 1))
    }

    const handleNext = async () => {
        const ok = current.fields ? await form.trigger(current.fields) : await form.trigger()
        if (ok) setActive((a) => a + 1)
    }

    const handleSubmit = form.handleSubmit((values) => {
        setSubmitError(null)
        startTransition(async () => {
            try {
                await onSubmit(values)
            } catch (e) {
                setSubmitError(e instanceof Error ? e.message : tStates('submitError'))
            }
        })
    })

    const progressLabel = tStep('stepProgress', { current: active + 1, total: steps.length })

    return (
        <FormProvider {...form}>
            <Stack gap='xl' miw={0}>
                <Box className={classes.progressTrack} miw={0}>
                    <Flex
                        align='center'
                        gap={{ base: 12, sm: 14 }}
                        role='group'
                        aria-label={progressLabel}
                        wrap='nowrap'
                        miw={0}>
                        {steps.map((s, index) => {
                            const isDone = index < active
                            const isCurrent = index === active
                            const segmentClass = [
                                classes.segment,
                                isDone ? classes.segmentDone : '',
                                isCurrent ? classes.segmentActive : ''
                            ]
                                .filter(Boolean)
                                .join(' ')
                            return <Box key={s.key} className={segmentClass} aria-hidden />
                        })}
                    </Flex>
                </Box>

                <Stack gap='xl' miw={0}>
                    <Title
                        order={2}
                        fw={600}
                        m={0}
                        fz={{ base: '1.65rem', sm: 'clamp(1.75rem, 4vw, 2.125rem)' }}
                        lh={1.2}
                        style={{ fontFamily: 'var(--eltrust-font-display)' }}>
                        {current.label}
                    </Title>

                    <Box key={current.key} className={classes.stepBody} miw={0}>
                        <StepComponent form={form} />
                    </Box>
                </Stack>

                {submitError ? (
                    <Text role='alert' size='md' c='red' fw={500} lh={1.5}>
                        {submitError}
                    </Text>
                ) : null}

                <Stack gap='md' mt={{ base: 'sm', sm: 'md' }}>
                    {isLast ? (
                        <Button size='xl' fullWidth onClick={handleSubmit} loading={submitting}>
                            {submitLabel ?? t('submit')}
                        </Button>
                    ) : (
                        <Button size='xl' fullWidth onClick={handleNext} disabled={submitting}>
                            {t('continue')}
                        </Button>
                    )}
                    {active > 0 || onCancel ? (
                        <Button
                            variant='subtle'
                            color='gray'
                            size='lg'
                            fullWidth
                            onClick={() => (active === 0 ? onCancel?.() : goBack())}
                            disabled={submitting}>
                            {active === 0 ? t('cancel') : t('back')}
                        </Button>
                    ) : null}
                </Stack>
            </Stack>
        </FormProvider>
    )
}
